import { Trash2 } from 'lucide-react';
import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { Table } from 'shared';
import { ExpenseEditMetaFlyover } from 'v1/components/shared';
import { Button } from 'v5/design-system/Button.js';

export interface BookingCostsSummaryTableProps<Rate> {
  title: string;
  data: {
    total?: string;
    rate: {
      name: string;
    } & Rate;
  }[];
  editableMeta: boolean;
  handleAdd?: () => void;
  handleRemove?: (id: string) => void;
  renderRateFlyover: (rate: Rate) => ReactNode;
}

const BookingCostsSummaryTable = <Rate,>({
  title,
  data,
  editableMeta,
  handleAdd,
  handleRemove,
  renderRateFlyover
}: BookingCostsSummaryTableProps<Rate>) => {
  const renderRateFlyoverRef = useRef(renderRateFlyover);
  const stableRenderRateFlyover = useCallback(
    ({ value }: { value: Rate }) => renderRateFlyoverRef.current(value),
    []
  );
  const columns = useMemo(
    () => [
      {
        Header: title,
        accessor: 'desc',
        border: true,
        cellPadding: value => (editableMeta ? '0' : value ? 'S' : 'M'),
        width: 120,
        Cell: ({ row: { original } }) =>
          editableMeta ? (
            // todo this is something we shuold get rid of because it can change the data directly
            <ExpenseEditMetaFlyover expense={original.rate} />
          ) : (
            <div className="text-13-600">{original.rate.name}</div>
          )
      },
      {
        id: 'cost',
        border: true,
        cellPadding: '0',
        width: 200,
        accessor: 'rate',
        Cell: stableRenderRateFlyover
      },
      {
        id: 'total',
        border: true,
        cellPadding: 'M',
        width: 82,
        accessor: 'total',
        Cell: ({ value }) =>
          value ? <div className="text-13-600">{value}</div> : null
      },
      {
        id: 'action',
        width: 40,
        cellPadding: '0',
        canResize: false,
        accessor: 'id',
        Cell: ({ value }) =>
          handleRemove ? (
            <Button
              className="m-[5px]"
              variant="ghost"
              size="sm"
              layout="icon"
              onClick={() => handleRemove(value)}
            >
              <Trash2 />
            </Button>
          ) : null
      }
    ],
    [title, editableMeta, handleRemove, stableRenderRateFlyover]
  );

  return (
    <Table
      cellPadding="S"
      rounded={false}
      flush
      columns={columns}
      data={data}
      onRowAdd={handleAdd}
      subRows={undefined}
    />
  );
};

export default BookingCostsSummaryTable;
